import { NextRouter } from "next/router";
import {
  EntitiesState,
  FrameworkHierarchy,
} from "@/contexts/entities/EntitiesContext";
import {
  Activity,
  ApiCompetency,
  ApiLearnerOutcome,
  ApiOutcomeArea,
  SubCompetency,
} from "./models";

export const getVideoHostUrl = () => {
  if (process.env.NEXT_PUBLIC_VERCEL_ENV === "production") {
    return "https://xqnavigator.wpengine.com/wp-content/uploads";
  }
  return "https://xqnavigatordev.wpengine.com/wp-content/uploads";
};

export const getPerformanceLevelHierarchyFromId = (id: string): string => {
  const performanceLevelIndicator = id.slice(-1);
  switch (performanceLevelIndicator) {
    case "1":
      return "Emerging";
    case "2":
      return "Developing";
    case "3":
      return "Proficient";
    case "4":
      return "Applying";
    default:
      return "";
  }
};

export type Breadcrumbs = {
  learnerOutcome: ApiLearnerOutcome | null;
  outcomeArea: ApiOutcomeArea | null;
  competency: ApiCompetency | null;
  subCompetency: SubCompetency | null;
  performanceLevel: {
    id: string;
    name: string;
  } | null;
  activity: Activity | null;
};

export const getBreadcrumbsFromId = (
  learnerOutcomes: ApiLearnerOutcome[],
  id: string,
) => {
  const breadcrumbs: Breadcrumbs = {
    learnerOutcome: null,
    outcomeArea: null,
    competency: null,
    subCompetency: null,
    performanceLevel: null,
    activity: null,
  };

  for (const learnerOutcome of learnerOutcomes) {
    for (const outcomeArea of learnerOutcome.outcomeAreas) {
      for (const competency of outcomeArea.competencies) {
        for (const subCompetency of competency.subCompetencies) {
          for (const performanceLevelId of [
            subCompetency.performanceLevel1Id,
            subCompetency.performanceLevel2Id,
            subCompetency.performanceLevel3Id,
            subCompetency.performanceLevel4Id,
          ]) {
            if (id.startsWith(learnerOutcome.id)) {
              breadcrumbs.learnerOutcome = learnerOutcome;
            }
            if (id.startsWith(competency.id)) {
              breadcrumbs.outcomeArea = outcomeArea;
              breadcrumbs.competency = competency;
            }
            if (id.startsWith(subCompetency.id)) {
              breadcrumbs.subCompetency = subCompetency;
            }
            if (id === performanceLevelId) {
              breadcrumbs.performanceLevel = {
                id: performanceLevelId,
                name: getPerformanceLevelHierarchyFromId(performanceLevelId),
              };
            }
          }
        }
      }
    }
  }

  return breadcrumbs;
};

export const getSelectionQueryParamFromRouter = (router: NextRouter) => {
  return router.query.selection;
};

export const createShareUrlWithSelectionQueryParam = (id: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set("selection", id);
  url.searchParams.set("utm_source", "shared_link");
  return url.toString();
};

export const createShareUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("utm_source", "shared_link");
  return url.toString();
};

export const sortByHierarchyOrder = (referenceArray: SubCompetency[]) => {
  return (a: FrameworkHierarchy, b: FrameworkHierarchy) => {
    const index1 = referenceArray.findIndex(
      (componentSkill) => componentSkill.id === a.subCompetency!.id,
    );
    const index2 = referenceArray.findIndex(
      (componentSkill) => componentSkill.id === b.subCompetency!.id,
    );
    return index1 - index2;
  };
};

export const getCompetencyId = (id: string) => {
  const result = id.match(/^(?:[^.]*\.){2}[^.]*/);
  return result ? result[0] : "";
};

export const normalizeEntities = (
  nestedLearnerOutcomes: ApiLearnerOutcome[],
): EntitiesState => {
  const nestedOutcomeAreas = nestedLearnerOutcomes.flatMap(
    (learnerOutcome) => learnerOutcome.outcomeAreas,
  );
  const nestedCompetencies = nestedOutcomeAreas.flatMap(
    (outcomeArea) => outcomeArea.competencies,
  );

  const learnerOutcomes = nestedLearnerOutcomes.map((lo) => ({
    ...lo,
    outcomeAreas: lo.outcomeAreas.map((oa) => {
      const childId = oa.competencies[0].id;
      const outcomeAreaId = childId.split(".").slice(0, 2).join(".");
      return outcomeAreaId;
    }),
  }));

  const outcomeAreas = nestedOutcomeAreas.map((outcomeArea) => {
    const childId = outcomeArea.competencies[0].id;
    const outcomeAreaId = childId.split(".").slice(0, 2).join(".");
    return {
      ...outcomeArea,
      id: outcomeAreaId,
      competencies: outcomeArea.competencies.map((c) => c.id),
    };
  });

  const competencies = nestedCompetencies.map((competency) => ({
    ...competency,
    subCompetencies: competency.subCompetencies.map((sc) => sc.id),
  }));

  const subCompetencies = nestedCompetencies.flatMap(
    (competency) => competency.subCompetencies,
  );

  const activities = subCompetencies.flatMap(
    (subCompetency) => subCompetency.activities,
  );

  return {
    learnerOutcomes,
    outcomeAreas,
    competencies,
    subCompetencies,
    activities,
  };
};

export const prepareCsvString = (rows: string[][]) => {
  return rows
    .map((row) =>
      row
        .map((v) => v.replaceAll('"', '""')) // Escape potential double quotes nested within any values
        .map((v) => `"${v}"`) // Quote each value (prevents splitting if value contains separator)
        .join(","),
    )
    .join("\r\n");
};

export const downloadProjectDetails = (content: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.setAttribute("href", url);
  downloadLink.setAttribute("download", "project-details.csv");
  downloadLink.click();
};

const getSubcompetencyDataRow = (
  subcompetencyId: string,
  getEntityHierarchyFromId: (id: string) => FrameworkHierarchy,
) => {
  const { learnerOutcome, outcomeArea, competency, subCompetency } =
    getEntityHierarchyFromId(subcompetencyId);
  return [
    learnerOutcome?.name,
    outcomeArea?.name,
    competency?.name,
    subCompetency?.name,
    subCompetency?.performanceLevel1Description,
    subCompetency?.performanceLevel2Description,
    subCompetency?.performanceLevel3Description,
    subCompetency?.performanceLevel4Description,
  ];
};

export const getProjectCsvData = (
  setMemberIds: string[],
  getEntityHierarchyFromId: (id: string) => FrameworkHierarchy,
) => {
  const headerRow = [
    "Learner Outcome",
    "Domain",
    "Competency",
    "Component Skill",
    "Emerging",
    "Developing",
    "Proficient",
    "Applying",
  ];
  const dataRows = setMemberIds.map((id) =>
    getSubcompetencyDataRow(id, getEntityHierarchyFromId),
  );
  return [headerRow, ...dataRows] as string[][];
};

export const sortSubCompetencyById = (a: SubCompetency, b: SubCompetency) => {
  if (a.id < b.id) {
    return -1;
  } else if (a.id > b.id) {
    return 1;
  }

  return 0;
};

export const sortBy = (a: string, b: string) => {
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  }

  return 0;
};
